import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSLPModal({ isOpen, onClose, user, projectName }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast.error('Please add a message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact-slp`, {
        ...formData,
        project_name: projectName || 'Not specified'
      });
      
      toast.success("Message sent! The SLP team will be in touch soon.");
      onClose();
      setFormData(prev => ({ ...prev, message: '' }));
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="contact-slp-modal"
        >
          {/* Header */}
          <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-slate-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Chat with SLP</h2>
                <p className="text-sm text-slate-400">We're here to help</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="close-contact-modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-slate-600 text-sm">
              Sometimes a quick conversation can save hours of going back and forth. 
              Drop us a note and we'll get back to you shortly.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@company.com"
                  required
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">How can we help?</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us what you're working on or where you're getting stuck..."
                  rows={4}
                  required
                  data-testid="contact-message-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
              data-testid="send-contact-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              We typically respond within a few hours during business hours (AEST)
            </p>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
